/**
 * Past Items Page
 * Browse previously used items and quickly re-add them to your list
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { itemsAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ThemeToggle from '@/components/ui/ThemeToggle';
import toast from 'react-hot-toast';

const PastItemsPage = () => {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [adding, setAdding] = useState({}); // map of name -> boolean

  // Debounce search term
  const [debounced, setDebounced] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = async (q = '') => {
    try {
      setLoading(true);
      const res = await itemsAPI.getHistory(q, 100);
      // getHistory returns { success, data: { suggestions } } from API client
      const data = res?.data?.suggestions || res?.suggestions || [];
      setSuggestions(data);
    } catch (e) {
      console.error('Failed to load history', e);
      // History errors are already softened in backend; avoid noisy toasts
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load('');
  }, []);

  useEffect(() => {
    load(debounced);
  }, [debounced]);

  const handleReuse = async (sugg) => {
    const name = sugg?.name?.trim();
    if (!name) return;
    setAdding((m) => ({ ...m, [name]: true }));
    try {
      await itemsAPI.reuseItem({
        name,
        quantity: sugg.quantity || 1,
        notes: sugg.notes || ''
      });
      toast.success(`Added "${name}" to your list`);
    } catch (e) {
      console.error('Failed to reuse item', e);
      toast.error('Could not add item');
    } finally {
      setAdding((m) => ({ ...m, [name]: false }));
    }
  };

  const visible = useMemo(() => suggestions, [suggestions]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-20">
      {/* Main */}
      <main className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Past Items
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Quickly re-add items you’ve used before.
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Search history</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search past items..."
              />
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner size="lg" text="Loading past items..." />
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span>Suggestions</span>
                <span className="text-sm font-normal text-gray-500">({visible.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {visible.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">No past items found.</p>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {visible.map((s, idx) => (
                    <li key={`${s.name}-${idx}`} className="py-3 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">{s.name}</span>
                          {s.quantity ? (
                            <span className="text-xs text-gray-500">x{s.quantity}</span>
                          ) : null}
                        </div>
                        {s.notes ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">{s.notes}</p>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleReuse(s)}
                          disabled={!!adding[s.name]}
                          loading={!!adding[s.name]}
                        >
                          Add
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default PastItemsPage;
